import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next"
import {
  Box,
  Typography,
  Alert,
  Paper,
  Button,
  CircularProgress
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import EntityForm from "../../components/EntityForm";
import { renderFormField } from "../../components/FormFields";
import api from "../../services/api";
import HorseLoader from "../../components/loadingHorse";
import { useCryptoNavigation, useAppNavigation } from "../../App";

interface Recorder {
  recorderId: number;
  recorderName: string;
  recorderModel: string;
  recorderChannels: number;
  recorderIntIP: string;
  recorderIntRTSP: number;
  recorderIntHTTP: number;
  recorderExtIP: string;
  recorderExtRTSP: number;
  recorderExtHTTP: number;
  recorderUser: string;
  recorderPass: string;
  recorderType?: number;
  recorderTypeName?: string;
}

export default function RecorderForm() {
  const { t } = useTranslation()
  const { navigateToPage } = useCryptoNavigation();
  const { getParam } = useAppNavigation();
  
  const id = getParam('id');
  const mode = getParam('mode');
  const tenantId = getParam('tenantId') || '1';
  
  const isEditMode = mode === 'edit' && Boolean(id);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setRetryCount(prev => prev + 1);
  };

  const fields = [
    {
      name: "recorderName",
      label: t("recorders.nameLabel"),
      type: "text" as const,
      required: true,
    },
    {
      name: "recorderModel",
      label: t("fields.model"),
      type: "text" as const,
      required: false,
    },
    {
      name: "recorderTypeName",
      label: t("recorders.typeLabel"),
      type: "select" as const,
      required: true,
      options: [
        { value: 'Hikvision', label: 'Hikvision' },
        { value: 'Intelbras', label: 'Intelbras' }
      ],
      helperText: t("recorders.typeHelper"),
    },
    ...(isEditMode
  ? [
      {
        name: "recorderStatus",
        label: t("recorders.statusLabel"),
        type: "select" as const,
        required: true,
        options: [
          { value: 1, label: t("common.active") },
          { value: 0, label: t("common.inactive") },
        ],
        helperText: t("recorders.statusHelper"),
      },
    ]
  : []),
    {
      name: "recorderChannels",
      label: t("recorders.channelsLabel"),
      type: "text" as const,
      required: true,
      placeholder: "16",
      helperText: t("recorders.channelsHelper"),
      InputProps: {
        inputProps: { 
          inputMode: 'numeric' as const,
          pattern: '[0-9]*',
        }
      },
    },
    {
      name: "recorderIntIP",
      label: t("recorders.intIP"),
      type: "text" as const,
      required: true,
      placeholder: "192.168.1.100",
      helperText: t("recorders.ipHelper"),
    },
    {
      name: "recorderIntHTTP",
      label: t("recorders.httpPortInt"),
      type: "text" as const,
      required: true,
      placeholder: "80",
      helperText: t("recorders.httpPortHelper"),
      InputProps: {
        inputProps: { 
          inputMode: 'numeric' as const,
          pattern: '[0-9]*',
        }
      },
    },
    {
      name: "recorderIntRTSP",
      label: t("recorders.rtspPortInt"),
      type: "text" as const,
      required: true,
      placeholder: "554",
      helperText: t("recorders.rtspPortHelper554"),
      InputProps: {
        inputProps: {
          inputMode: 'numeric' as const,
          pattern: '[0-9]*',
        }
      },
    },
    {
      name: "recorderExtIP",
      label: t("recorders.extIP"),
      type: "text" as const,
      required: true,
      placeholder: "192.168.1.100",
      helperText: t("recorders.ipHelper"),
    },
    {
      name: "recorderExtHTTP",
      label: t("recorders.httpPortExt"),
      type: "text" as const,
      required: true,
      placeholder: "8080",
      helperText: t("recorders.httpPortHelper8080"),
      InputProps: {
        inputProps: { 
          inputMode: 'numeric' as const,
          pattern: '[0-9]*',
        }
      },
    },
    {
      name: "recorderExtRTSP",
      label: t("recorders.rtspPortExt"),
      type: "text" as const,
      required: true,
      placeholder: "554",
      helperText: t("recorders.rtspPortHelper554"),
      InputProps: {
        inputProps: {
          inputMode: 'numeric' as const,
          pattern: '[0-9]*',
        }
      },
    },
    {
      name: "recorderUser",
      label: t("recorders.userLabel"),
      type: "text" as const,
      required: true,
      helperText: t("recorders.userHelper"),
    },
    {
      name: "recorderPass",
      label: t("recorders.passLabel"),
      type: "text" as const,
      required: true,
      helperText: t("recorders.passHelper"),
      InputProps: {
        type: "password",
        inputProps: {
          autoComplete: "new-password"
        }
      }
    },
  ];

  const validateNumericField = (fieldName: string, value: any): string | null => {
    if (!value && value !== 0) return null;
    
    const strValue = String(value).trim();
    
    if (!/^\d+$/.test(strValue)) {
      return t("recorders.validationOnlyPositive");
    }
    
    const numValue = Number(strValue);
    
    if (isNaN(numValue)) {
      return t("recorders.validationValidNumber");
    }
    
    if (numValue < 0) {
      return t("recorders.validationNoNegative");
    }
    
    if (numValue === 0) {
      return t("recorders.validationGreaterThanZero");
    }
    
    switch (fieldName) {
      case 'recorderChannels':
        if (numValue > 128) {
          return t("recorders.validationMaxChannels");
        }
        break;
        
      case 'recorderIntHTTP':
      case 'recorderIntRTSP':
      case 'recorderExtHTTP':
      case 'recorderExtRTSP':
        if (numValue > 65535) {
          return t("recorders.validationMaxPort");
        }
        break;
    }
    
    return null;
  };  

  const validateIP = (ip: string): string | null => {
    if (!ip) return null;
    
    const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
    
    if (!ipRegex.test(ip)) {
      return t("recorders.validationInvalidIP");
    }
    
    if (ip === "0.0.0.0") {
      return t("recorders.validationIPZero");
    }
    
    return null;
  };

  const validateForm = (formData: any) => {
    const errors: Record<string, string> = {};
    
    if (!formData.recorderTypeName || formData.recorderTypeName.trim() === '') {
      errors.recorderTypeName = t("recorders.validationTypeRequired");
    }
    
    const numericFields = [
      'recorderChannels',
      'recorderIntHTTP',
      'recorderIntRTSP',
      'recorderExtHTTP',
      'recorderExtRTSP'
    ];
    
    numericFields.forEach(fieldName => {
      const error = validateNumericField(fieldName, formData[fieldName]);
      if (error) {
        errors[fieldName] = error;
      }
    });
    
    const ipError1 = validateIP(formData.recorderIntIP);
    if (ipError1) {
      errors.recorderIntIP = ipError1;
    }
    
    const ipError2 = validateIP(formData.recorderExtIP);
    if (ipError2) {
      errors.recorderExtIP = ipError2;
    }
    
    if (!formData.recorderName?.trim()) {
      errors.recorderName = t("recorders.validationNameRequired");
    }
    
    if (!formData.recorderUser?.trim()) {
      errors.recorderUser = t("recorders.validationUserRequired");
    }
    
    if (!formData.recorderPass?.trim()) {
      errors.recorderPass = t("recorders.validationPassRequired");
    }
    
    return errors;
  };

  const transformBeforeSave = (formData: any) => {
    const cleanNumeric = (value: any, defaultValue: number): number => {
      if (!value) return defaultValue;
      const cleaned = String(value).replace(/[^\d]/g, '');
      const num = Number(cleaned);
      return (num && num > 0) ? num : defaultValue;
    };

    // Em edição o EntityForm passa APENAS os campos modificados (o diff). Por isso
    // só incluímos cada campo quando ele está presente — senão o backend receberia
    // defaults para campos intocados (ex.: tipo virando Hikvision, status virando
    // null quando o usuário só mudou outro campo).
    const out: any = {};
    if ('recorderName' in formData) out.recorderName = formData.recorderName || '';
    if ('recorderModel' in formData) out.recorderModel = formData.recorderModel || '';
    if ('recorderTypeName' in formData) {
      out.recorderType = formData.recorderTypeName === 'Intelbras' ? 2 : 1;
    }
    if ('recorderChannels' in formData) out.recorderChannels = cleanNumeric(formData.recorderChannels, 16);
    if ('recorderIntIP' in formData) out.recorderIntIP = formData.recorderIntIP || '';
    if ('recorderIntHTTP' in formData) out.recorderIntHTTP = cleanNumeric(formData.recorderIntHTTP, 80);
    if ('recorderIntRTSP' in formData) out.recorderIntRTSP = cleanNumeric(formData.recorderIntRTSP, 554);
    if ('recorderExtIP' in formData) out.recorderExtIP = formData.recorderExtIP || '';
    if ('recorderExtHTTP' in formData) out.recorderExtHTTP = cleanNumeric(formData.recorderExtHTTP, 8080);
    if ('recorderExtRTSP' in formData) out.recorderExtRTSP = cleanNumeric(formData.recorderExtRTSP, 554);
    if ('recorderUser' in formData) out.recorderUser = formData.recorderUser || '';
    if ('recorderPass' in formData) out.recorderPass = formData.recorderPass || '';
    // recorderStatus só existe como campo no modo edição; envia só se foi alterado.
    if ('recorderStatus' in formData) out.recorderStatus = Number(formData.recorderStatus);

    return out;
  };

  const handleSuccess = (data: any) => {
    navigateToPage('gravadores', { tenantId });
  };

  const handleError = (error: any) => {
    let errorMessage = t("recorders.saveError");
    
    if (error.message && !error.response) {
      errorMessage = error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.status === 500) {
      const errorStr = JSON.stringify(error.response.data);
      if (errorStr.includes('Duplicate entry')) {
        errorMessage = t("recorders.duplicateError");
      }
    }
    
    setError(errorMessage);
  };

  const transformAfterLoad = (data: any): any => {
    const recorderData = data.recorder || data;
    
    const ensurePositive = (value: any, defaultValue: number): number => {
      const num = Number(value);
      return (num && num > 0) ? num : defaultValue;
    };
    
    // Mapear ID para nome
    let recorderTypeName = 'Hikvision'; // Default
    if (recorderData.recorderType === 2) {
      recorderTypeName = 'Intelbras';
    }
    
    const transformedData = {
      recorderId: recorderData.recorderId,
      recorderName: recorderData.recorderName || '',
      recorderModel: recorderData.recorderModel || '',
      recorderTypeName: recorderTypeName,
      recorderChannels: String(ensurePositive(recorderData.recorderChannels, 16)),
      recorderIntIP: recorderData.recorderIntIP || '',
      recorderIntHTTP: String(ensurePositive(recorderData.recorderIntHTTP, 80)),
      recorderIntRTSP: String(ensurePositive(recorderData.recorderIntRTSP, 554)),
      recorderExtIP: recorderData.recorderExtIP || '',
      recorderExtHTTP: String(ensurePositive(recorderData.recorderExtHTTP, 8080)),
      recorderExtRTSP: String(ensurePositive(recorderData.recorderExtRTSP, 554)),
      recorderUser: recorderData.recorderUser || '',
      recorderPass: recorderData.recorderPass || '',
      recorderStatus: recorderData.recorderStatus ?? 0,
    };
    
    return transformedData;
  };

  return (
    <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" component="h1" gutterBottom>
        {isEditMode ? t("recorders.editTitle") : t("recorders.newTitle")}
      </Typography>
      
      {error && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRetry}
            >
              {t("recorders.retry")}
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      
      <EntityForm
        title={t("recorders.formTitle")}
        endpoint={`/recorder/${tenantId}`}
        fields={fields}
        listPath={`/recorder/${tenantId}`}
        listPageName="gravadores"
        renderField={renderFormField}
        transformBeforeSave={transformBeforeSave}
        transformAfterLoad={transformAfterLoad}
        validateForm={validateForm}
        onSuccess={handleSuccess}
        onError={handleError}
        idField="recorderId"
        useEncryptedNavigation={true}
        defaultTenantId={tenantId}
      />
    </Paper>
  );
}